// NAPOMENA:
//  - linija rute se selektira sa livim klikom
//  - tocka na liniji rute se selektira sa alt + livi klik

import "mapbox-gl/dist/mapbox-gl.css";
import proj4 from "proj4";
import {
  GeoJSONFeature,
  GeoJSONSource,
  LngLat,
  Map,
  MapMouseEvent,
  PointLike,
} from "mapbox-gl";
import React, { useRef, useEffect, useState } from "react";

import Walker from "./Walker.ts";
import { UpdateSelectedSound } from "./Selection.ts";
import styles from "./Mappanel.module.css";
import "mapbox-gl/dist/mapbox-gl.css";
import { RouteAction, ToolType, RouteActionKind } from "../../../Reducer.ts";
import {
  EffectRouteChange,
  EffectSimulationStates,
  EffectMapInitialization,
} from "./Mappanel.hooks.ts";
import { Point, RouteState } from "../../../api/edits/editRoute.responses.ts";
import { UpdateRouteLineVertices } from "./Routeline.ts";
import { LayerVisibilityMenu, LayerVisibilityState } from "./LayerVisibilityMenu.tsx";
import { usePoiPlayback } from "./usePoiPlayback.ts";
import { PoiPlayerOverlay } from "./PoiPlayerOverlay.tsx";

// definition utm33 projection
// ALL COORDINATES ARE IN WGS84 EXCEPT WHERE IT WAS EXPLICETELY STATED!
let utm33 =
  'PROJCS["WGS 84 / UTM zone 33N",GEOGCS["WGS 84",DATUM["WGS_1984",SPHEROID["WGS 84",6378137,298.257223563,AUTHORITY["EPSG","7030"]],AUTHORITY["EPSG","6326"]],PRIMEM["Greenwich",0,AUTHORITY["EPSG","8901"]],UNIT["degree",0.0174532925199433,AUTHORITY["EPSG","9122"]],AUTHORITY["EPSG","4326"]],PROJECTION["Transverse_Mercator"],PARAMETER["latitude_of_origin",0],PARAMETER["central_meridian",15],PARAMETER["scale_factor",0.9996],PARAMETER["false_easting",500000],PARAMETER["false_northing",0],UNIT["metre",1,AUTHORITY["EPSG","9001"]],AXIS["Easting",EAST],AXIS["Northing",NORTH],AUTHORITY["EPSG","32633"]]';

// is cursor hoovering over feature
let hover: boolean = false;
// is currently moving feature
let moving: boolean = false;
// feature that is being moved
let movedFeatureName: string;
let movedFeatureLngLat: LngLat;
let movedFeatureOriginLngLat: LngLat;

interface MapPanelProps {
  className: string;
  stateSelected: string;
  setSelected: React.Dispatch<React.SetStateAction<string>>;
  createNewFeature: (a: LngLat, shiftKey?: boolean) => void;
  openDialogBoxDelete: () => void;
  currentTool: ToolType;
  routeState: RouteState;
  routeDispatch: React.Dispatch<RouteAction>;
  currentLanguage: string;
  startLocation: [number, number];
  setStartLocation: React.Dispatch<React.SetStateAction<[number, number]>>;
}

const DEFAULT_ZOOM: number = 15;

/////////////////// MAPPANEL COMPONENT
export const Mappanel = (props: MapPanelProps) => {
  // for map animation
  const requestRef = React.useRef(0);
  const previousTimeRef = React.useRef();

  // mapbox declarations
  const mapContainer = useRef(null);
  // declared as object to pass by reference
  const map: React.MutableRefObject<{ value: null | Map }> = useRef({
    value: null,
  });

  const [zoom, setZoom] = useState(DEFAULT_ZOOM);

  const [layerVisibility, setLayerVisibility] = useState<LayerVisibilityState>({
    bsRange: true,
    fsRange: true,
    poiRange: true,
  });

  const toggleLayerVisibility = (key: keyof LayerVisibilityState) => {
    setLayerVisibility((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  // walker definition for auralization
  const myWalker = React.useRef(new Walker());

  const { poiState, onTogglePlayPause, onSkipForward } = usePoiPlayback(
    myWalker,
    props.routeState,
    props.currentTool === "start"
  );

  // animation handling functionx
  const animate = (time: any) => {
    if (previousTimeRef.current !== undefined) {
      const deltaTime = time - previousTimeRef.current;
      myWalker.current.CalcPosition(deltaTime);
      myWalker.current.CalcVolume();
      myWalker.current.UpdateWalker(map.current.value!!);
    }
    previousTimeRef.current = time;
    requestRef.current = requestAnimationFrame(animate);
  };

  // map initialization
  useEffect(() => {
    console.log("Map Panel effect: []");

    EffectMapInitialization(
      map.current,
      props.startLocation,
      props.routeState,
      myWalker.current,
      mapContainer.current!,
      zoom,
      props.setStartLocation,
      setZoom
    );

    // eslint-disable-next-line
  }, []);

  //Change the map center to the new center
  useEffect(() => {
    if (map.current.value) {
      map.current.value.flyTo({
        center: props.startLocation,
      });
    }
    if (myWalker.current) {
      myWalker.current.SetLngLat(
        props.startLocation[0],
        props.startLocation[1]
      );
    }
  }, [props.startLocation]);

  // update the rectangle around the selected sound
  useEffect(() => {
    console.log("Map Panel effect: [props.stateSelected, props.stateRoute]");
    UpdateSelectedSound(
      map.current.value!!,
      props.routeState,
      props.stateSelected
    );
    // eslint-disable-next-line
  }, [props.stateSelected, props.routeState]);

  // refreshing the onClick() handler
  useEffect(() => {
    console.log("Map Panel effect: [props.currentTool, props.stateRoute]");

    // post:  - if cursor moves over feature:
    //          - if tool is select or delete:
    //            - it is changed to pointer
    //          - if tool is move:
    //            - it is changed to move cross
    function MouseEnterHandler(e: MapMouseEvent) {
      if (props.currentTool === "select") {
        map.current.value!!.getCanvas().style.cursor = "pointer";
        hover = true;
        map.current.value!!.dragPan.disable();
      } else if (props.currentTool === "delete") {
        map.current.value!!.getCanvas().style.cursor = "pointer";
        hover = true;
        map.current.value!!.dragPan.disable();
      } else if (props.currentTool === "move") {
        map.current.value!!.getCanvas().style.cursor = "move";
        hover = true;
        map.current.value!!.dragPan.disable();
      }
    }

    // post:  - if cursor leaves the feature:
    //          - if tool is draw
    //            - it is changed to crosshair
    //          - if tool is select, delete or move
    //            - it is changed to default
    function MouseLeaveHandler(e: MapMouseEvent) {
      if (props.currentTool === "draw") {
        map.current.value!.getCanvas().style.cursor = "crosshair";
        hover = false;
        map.current.value!.dragPan.disable();
      } else if (
        props.currentTool === "select" ||
        props.currentTool === "delete"
      ) {
        map.current.value!.getCanvas().style.cursor = "default";
        hover = false;
        map.current.value!.dragPan.enable();
      } else if (props.currentTool === "move" && moving !== true) {
        map.current.value!.getCanvas().style.cursor = "default";
        hover = false;
        map.current.value!.dragPan.enable();
      }
    }

    // post:  - if map is clicked
    //          - if cursor was over feature
    //            - if tool was select it is selected
    //            - if tool was delete dialog box for delete is displayed
    //          - if cursor was not over feature
    //            - if tool was select or delete, selection is canceled
    //            - if tool was new dialog box for new is displayed
    function MouseClickHandler(e: MapMouseEvent) {
      const bbox: [PointLike, PointLike] = [
        [e.point.x - 5, e.point.y - 5],
        [e.point.x + 5, e.point.y + 5],
      ];
      const selectedFeatures = map.current.value!.queryRenderedFeatures(bbox);

      // if hovering
      if (hover === true) {
        if (props.currentTool === "select") {
          let ts = TranslateSelection(
            props.routeState,
            selectedFeatures,
            e.lngLat,
            e.originalEvent.altKey
          );
          props.setSelected(ts);
        } else if (props.currentTool === "delete") {
          let ts = TranslateSelection(
            props.routeState,
            selectedFeatures,
            e.lngLat,
            e.originalEvent.altKey
          );
          props.setSelected(ts);
          props.openDialogBoxDelete();
        }
      } else {
        if (props.currentTool === "select" || props.currentTool === "delete") {
          props.setSelected("rprop");
        } else if (props.currentTool === "draw") {
          props.createNewFeature(e.lngLat, e.originalEvent.shiftKey);
        }
      }
    }

    // post:  - if mouse button was pressed
    //          - if cursor was over feature
    //            - if tool was move
    //              - it is selected
    //              - cursor is set to crosshair
    //              - moving flag is set
    function MouseDownHandler(e: MapMouseEvent) {
      const bbox: [PointLike, PointLike] = [
        [e.point.x - 5, e.point.y - 5],
        [e.point.x + 5, e.point.y + 5],
      ];
      const selectedFeatures = map.current.value!.queryRenderedFeatures(bbox);

      // if hovering
      if (hover === true) {
        if (props.currentTool === "move") {
          movedFeatureName = TranslateSelection(
            props.routeState,
            selectedFeatures,
            e.lngLat,
            e.originalEvent.shiftKey
          );
          movedFeatureOriginLngLat = e.lngLat;
          map.current.value!.getCanvas().style.cursor = "crosshair";
          moving = true;
        }
      }
    }

    // post:  - if mouse button was released
    //          - if cursor was over feature
    //            - if tool was move
    //            - it is selected and cursor is crosshair
    function MouseUpHandler(e: MapMouseEvent) {
      // if moving
      if (moving) {
        map.current.value!.getCanvas().style.cursor = "move";
        moving = false;
        movedFeatureLngLat = e.lngLat;
        MoveFeature(
          props.routeDispatch,
          movedFeatureName,
          movedFeatureLngLat,
          movedFeatureOriginLngLat
        );
      }
    }

    // post:  - during drag in move mode, updates the feature preview in real time
    //          by directly setting Mapbox source data (bypasses React state for smoothness)
    function MouseMoveHandler(e: MapMouseEvent) {
      if (!moving) return;

      if (movedFeatureName.substring(0, 4) === "pois") {
        // Dragging a POI — update point source directly
        const draggedId = Number(movedFeatureName.substring(4));
        const poiSrc = map.current.value!.getSource(
          "points_POIs_source"
        ) as GeoJSONSource;
        if (poiSrc) {
          poiSrc.setData({
            type: "FeatureCollection",
            features: props.routeState.pointsOfInterest.map((poi) => ({
              type: "Feature",
              geometry: {
                type: "Point",
                coordinates:
                  poi.id === draggedId
                    ? [e.lngLat.lng, e.lngLat.lat]
                    : [poi.longitude, poi.latitude],
              },
              properties: {
                title: String(poi.id),
                order: poi.pointOrder,
              },
            })),
          });
        }
      } else if (movedFeatureName.substring(0, 3) === "bss") {
        // Dragging a background sound — update point source and circle polygon
        const draggedId = Number(movedFeatureName.substring(3));
        const previewCoords = props.routeState.backgroundSounds.map((bs) =>
          bs.id === draggedId
            ? [e.lngLat.lng, e.lngLat.lat]
            : [bs.longitude, bs.latitude]
        );
        const bsPointSrc = map.current.value!.getSource(
          "points_background_sounds_source"
        ) as GeoJSONSource;
        if (bsPointSrc) {
          bsPointSrc.setData({
            type: "FeatureCollection",
            features: props.routeState.backgroundSounds.map((bs, i) => ({
              type: "Feature",
              geometry: { type: "Point", coordinates: previewCoords[i] },
              properties: { title: bs.name },
            })),
          });
        }
        const bsPolySrc = map.current.value!.getSource(
          "bs_polygon"
        ) as GeoJSONSource;
        if (bsPolySrc) {
          bsPolySrc.setData({
            type: "Feature",
            geometry: {
              type: "MultiPolygon",
              coordinates: props.routeState.backgroundSounds.map((bs, i) => [
                CreateCirclePolygon(previewCoords[i], bs.radius),
              ]),
            },
          } as any);
        }
      } else if (movedFeatureName.substring(0, 3) === "fss") {
        // Dragging a focused sound — update point source and circle polygon
        const draggedId = Number(movedFeatureName.substring(3));
        const previewCoords = props.routeState.focusedSounds.map((fs) =>
          fs.id === draggedId
            ? [e.lngLat.lng, e.lngLat.lat]
            : [fs.longitude, fs.latitude]
        );
        const fsPointSrc = map.current.value!.getSource(
          "points_focused_sounds_source"
        ) as GeoJSONSource;
        if (fsPointSrc) {
          fsPointSrc.setData({
            type: "FeatureCollection",
            features: props.routeState.focusedSounds.map((fs, i) => ({
              type: "Feature",
              geometry: { type: "Point", coordinates: previewCoords[i] },
              properties: { title: fs.name },
            })),
          });
        }
        const fsPolySrc = map.current.value!.getSource(
          "fs_polygon"
        ) as GeoJSONSource;
        if (fsPolySrc) {
          fsPolySrc.setData({
            type: "Feature",
            geometry: {
              type: "MultiPolygon",
              coordinates: props.routeState.focusedSounds.map((fs, i) => [
                CreateCirclePolygon(previewCoords[i], fs.radius),
              ]),
            },
          } as any);
        }
      } else if (movedFeatureName.substring(0, 5) === "rline") {
        // Dragging route line or a vertex
        let previewPoints: Point[];

        if (movedFeatureName.length > 5) {
          // Dragging a single vertex
          const vertexIndex = Number(movedFeatureName.substring(5));
          previewPoints = props.routeState.linePoints.map((pt, i) =>
            i === vertexIndex
              ? { latitude: e.lngLat.lat, longitude: e.lngLat.lng }
              : pt
          );
        } else {
          // Dragging the whole line — apply delta from origin
          const deltaLng = e.lngLat.lng - movedFeatureOriginLngLat.lng;
          const deltaLat = e.lngLat.lat - movedFeatureOriginLngLat.lat;
          previewPoints = props.routeState.linePoints.map((pt) => ({
            latitude: pt.latitude + deltaLat,
            longitude: pt.longitude + deltaLng,
          }));
        }

        const lineCoords = previewPoints.map((pt) => [pt.longitude, pt.latitude]);

        const lineSrc = map.current.value!.getSource(
          "route_line_source"
        ) as GeoJSONSource;
        if (lineSrc) {
          lineSrc.setData({
            type: "Feature",
            properties: {},
            geometry: { type: "LineString", coordinates: lineCoords },
          });
        }

        const vertexSrc = map.current.value!.getSource(
          "route_line_vertices_source"
        ) as GeoJSONSource;
        if (vertexSrc) {
          vertexSrc.setData({
            type: "FeatureCollection",
            features: previewPoints.map((pt) => ({
              type: "Feature",
              geometry: {
                type: "Point",
                coordinates: [pt.longitude, pt.latitude],
              },
              properties: {},
            })),
          });
        }
      }
    }

    // post:  - right-click on a vertex handle in draw mode deletes that vertex
    function MouseContextMenuHandler(e: MapMouseEvent) {
      if (props.currentTool !== "draw") return;
      e.originalEvent.preventDefault();
      const bbox: [PointLike, PointLike] = [
        [e.point.x - 8, e.point.y - 8],
        [e.point.x + 8, e.point.y + 8],
      ];
      const features = map.current.value!.queryRenderedFeatures(bbox, {
        layers: ["route_line_vertices"],
      });
      if (features.length > 0) {
        const clickPnt: Point = {
          latitude: e.lngLat.lat,
          longitude: e.lngLat.lng,
        };
        const vertexIndex = FindClosestPoint(props.routeState, clickPnt);
        if (props.routeState.linePoints.length <= 2) {
          props.routeDispatch({
            type: RouteActionKind.DELETE_ROUTELINE,
            payload: { id: 0 },
          });
        } else {
          props.routeDispatch({
            type: RouteActionKind.DELETE_ROUTELINEPOINT,
            payload: { id: vertexIndex },
          });
        }
        props.setSelected("rline");
      }
    }

    // mount and dismount of MouseClickHandler
    if (map.current.value!) {
      if (props.currentTool === "draw") {
        map.current.value!.getCanvas().style.cursor = "crosshair";
        map.current.value!.dragPan.disable();
      } else {
        map.current.value!.getCanvas().style.cursor = "default";
        map.current.value!.dragPan.enable();
      }

      // Show vertex handles when in draw mode
      UpdateRouteLineVertices(
        map.current.value!,
        props.routeState,
        props.currentTool === "draw"
      );

      const interactiveLayers = [
        "points_focused_sounds",
        "points_background_sounds",
        "points_POIs",
        "route_line",
        "line_selection",
        "route_line_vertices",
      ];

      map.current.value!.on("click", MouseClickHandler);
      map.current.value!.on("mouseenter", interactiveLayers, MouseEnterHandler);
      map.current.value!.on("mouseleave", interactiveLayers, MouseLeaveHandler);
      map.current.value!.on("mousedown", interactiveLayers, MouseDownHandler);
      map.current.value!.on("mouseup", MouseUpHandler);
      map.current.value!.on("mousemove", MouseMoveHandler);
      map.current.value!.on("contextmenu", MouseContextMenuHandler);
    }
    return () => {
      hover = false;
      moving = false;
      if (map.current.value!) {
        const interactiveLayers = [
          "points_focused_sounds",
          "points_background_sounds",
          "points_POIs",
          "route_line",
          "line_selection",
          "route_line_vertices",
        ];

        map.current.value!.off("click", MouseClickHandler);
        map.current.value!.off(
          "mouseenter",
          interactiveLayers,
          MouseEnterHandler
        );
        map.current.value!.off(
          "mouseleave",
          interactiveLayers,
          MouseLeaveHandler
        );
        map.current.value!.off(
          "mousedown",
          interactiveLayers,
          MouseDownHandler
        );
        // eslint-disable-next-line
        map.current.value!.off("mouseup", MouseUpHandler);
        map.current.value!.off("mousemove", MouseMoveHandler);
        map.current.value!.off("contextmenu", MouseContextMenuHandler);
      }
    };
    // eslint-disable-next-line
  }, [props.currentTool, props.routeState, props.stateSelected]);

  // handles key actions
  function keyHandler(event: React.KeyboardEvent<HTMLDivElement>) {
    // walker simulation controls
    if (event.key === "s") {
      myWalker.current.RotateCWise();
    }
    if (event.key === "p") {
      myWalker.current.Walk();
    }
    if (event.key === "a") {
      myWalker.current.RotateCCWise();
    }
    if (event.key === "l") {
      myWalker.current.SlowDown();
    }

  }

  // simulation states
  useEffect(() => {
    console.log("Map Panel effect: [props.currentTool]");
    EffectSimulationStates(
      props.currentTool,
      myWalker.current,
      props.routeState,
      requestRef.current,
      animate,
      map.current.value!,
      props.setSelected,
      props.currentLanguage
    );
    // eslint-disable-next-line
  }, [props.currentTool]);

  // refreshing map on route change
  useEffect(() => {
    console.log("Map Panel effect: [props.stateRoute]");
    EffectRouteChange(
      map.current.value!,
      props.routeState,
      myWalker.current,
      props.currentLanguage,
      props.currentTool
    );
  }, [props.routeState, props.currentLanguage]);

  useEffect(() => {
    const m = map.current.value;
    if (!m) return;
    const toggle = (layerIds: string[], visible: boolean) => {
      for (const id of layerIds) {
        if (m.getLayer(id)) m.setLayoutProperty(id, "visibility", visible ? "visible" : "none");
      }
    };
    toggle(["bs_fill", "bs_line"], layerVisibility.bsRange);
    toggle(["fs_fill", "fs_line"], layerVisibility.fsRange);
    toggle(["poi_fill", "poi_line"], layerVisibility.poiRange);
  }, [layerVisibility]);

  return (
    <div className="relative">
      <div
        id="myMap"
        ref={mapContainer}
        className={styles.mapContainer}
        onKeyDown={keyHandler}
        tabIndex={0}
      />
      <div className="absolute top-3 right-12 z-10">
        <LayerVisibilityMenu layerVisibility={layerVisibility} onToggle={toggleLayerVisibility} />
      </div>
      {poiState && (
        <div className="absolute bottom-4 left-4 z-10">
          <PoiPlayerOverlay
            poiState={poiState}
            onTogglePlayPause={onTogglePlayPause}
            onSkipForward={onSkipForward}
          />
        </div>
      )}
      <pre id="info"></pre>
    </div>
  );
};

// pre:   - route contains route data
//        - selectedFeatures contains the selected MapBox features
//        - selectionPoint is point on which user clicked when selecting
// post:  returns string with selected point in the format of treepanel id
export function TranslateSelection(
  route: RouteState,
  selectedFeatures: GeoJSONFeature[],
  selectionPoint: LngLat,
  altPressed: boolean
): string {
  if (selectedFeatures.length > 0) {
    if (selectedFeatures[0].layer!.id === "points_focused_sounds") {
      for (const fs of route.focusedSounds) {
        if (
          selectedFeatures[0].properties != null &&
          fs.name === selectedFeatures[0].properties.title
        ) {
          return "fss" + fs.id;
        }
      }
    }
    if (selectedFeatures[0].layer!.id === "points_background_sounds") {
      for (const bs of route.backgroundSounds) {
        if (
          selectedFeatures[0].properties != null &&
          bs.name === selectedFeatures[0].properties.title
        ) {
          return "bss" + bs.id;
        }
      }
    }
    if (selectedFeatures[0].layer!.id === "points_POIs") {
      for (let i: number = 0; i < route.pointsOfInterest.length; ++i) {
        if (
          selectedFeatures[0].properties != null &&
          route.pointsOfInterest[i].id ===
            Number(selectedFeatures[0].properties.title)
        ) {
          return "pois" + route.pointsOfInterest[i].id;
        }
      }
    }
    if (selectedFeatures[0].layer!.id === "route_line" && !altPressed) {
      return "rline";
    } else {
      return (
        "rline" +
        String(
          FindClosestPoint(route, {
            latitude: selectionPoint.lat,
            longitude: selectionPoint.lng,
          })
        )
      );
    }
  }
  return "rprop";
}

// pre:   - route contains route data
//        - selectionPoint is point on which user clicked when selecting
// post:  returns index of closest point on route_line
export function FindClosestPoint(
  route: RouteState,
  selectionPoint: Point
): number {
  let minSqDist: number = SquaredDistance(
    proj4(utm33).forward([
      route.linePoints[0].longitude,
      route.linePoints[0].latitude,
    ]),
    proj4(utm33).forward([selectionPoint.longitude, selectionPoint.latitude])
  );
  let minSqDistIndex: number = 0;
  for (let i: number = 0; i < route.linePoints.length; ++i) {
    let dist: number = SquaredDistance(
      proj4(utm33).forward([
        route.linePoints[i].longitude,
        route.linePoints[i].latitude,
      ]),
      proj4(utm33).forward([selectionPoint.longitude, selectionPoint.latitude])
    );
    if (dist < minSqDist) {
      minSqDist = dist;
      minSqDistIndex = i;
    }
  }
  return minSqDistIndex;
}

// pre:   - route has the route with focused sounds
//        - select has the name of the selected entry from treepanel
// post:  returns array data with coordinates of focused sounds
export function MoveFeature(
  routeDispatch: React.Dispatch<RouteAction>,
  movedFeatureName: string,
  movedFeatureLngLat: LngLat,
  movedFeatureOriginLngLat: LngLat
): void {
  // detect layer
  if (movedFeatureName.substring(0, 3) === "bss") {
    let str: string = movedFeatureName.substring(3);
    routeDispatch({
      type: RouteActionKind.MOVE_BACKGROUND,
      payload: {
        id: Number(str),
        pnt: {
          latitude: movedFeatureLngLat.lat,
          longitude: movedFeatureLngLat.lng,
        },
      },
    });
  } else if (movedFeatureName.substring(0, 3) === "fss") {
    let str: string = movedFeatureName.substring(3);
    routeDispatch({
      type: RouteActionKind.MOVE_FOCUSED,
      payload: {
        id: Number(str),
        pnt: {
          latitude: movedFeatureLngLat.lat,
          longitude: movedFeatureLngLat.lng,
        },
      },
    });
  } else if (movedFeatureName.substring(0, 4) === "pois") {
    let str: string = movedFeatureName.substring(4);
    routeDispatch({
      type: RouteActionKind.MOVE_POI,
      payload: {
        id: Number(str),
        pnt: {
          latitude: movedFeatureLngLat.lat,
          longitude: movedFeatureLngLat.lng,
        },
      },
    });
  } else if (movedFeatureName.substring(0, 5) === "rline") {
    let deltaLngLat: LngLat = new LngLat(
      movedFeatureLngLat.lng - movedFeatureOriginLngLat.lng,
      movedFeatureLngLat.lat - movedFeatureOriginLngLat.lat
    );
    if (movedFeatureName.length === 5) {
      routeDispatch({
        type: RouteActionKind.MOVE_ROUTELINE,
        payload: {
          pnt: {
            latitude: deltaLngLat.lat,
            longitude: deltaLngLat.lng,
          },
        },
      });
    } else {
      let str: string = movedFeatureName.substring(5);
      routeDispatch({
        type: RouteActionKind.MOVE_ROUTELINEPOINT,
        payload: {
          id: Number(str),
          pnt: {
            latitude: movedFeatureLngLat.lat,
            longitude: movedFeatureLngLat.lng,
          },
        },
      });
    }
  }
}

// pre:   - point1 and point 2 to measure distance
// post:  - squared distance in meters utm33
export function SquaredDistance(point1: number[], point2: number[]): number {
  return (
    (point1[0] - point2[0]) * (point1[0] - point2[0]) +
    (point1[1] - point2[1]) * (point1[1] - point2[1])
  );
}

// pre:   - lngLat with coordinates of the circle center
//        - radius with radius of the circle in meters
// post:  - returns an array with coordinates of the circle polygon
export function CreateCirclePolygon(
  lngLat: number[],
  radius: number
): number[][] {
  // consts for drawing circles
  const NUM_VERTICES: number = radius * 2;

  const DELTA_THETA: number = (2 * Math.PI) / NUM_VERTICES;
  let utm33coord: number[] = proj4(utm33).forward([
    parseFloat(String(lngLat[0])),
    parseFloat(String(lngLat[1])),
  ]);
  let coords: number[][] = [];
  for (let i: number = 0; i <= NUM_VERTICES; i++) {
    let t: number = i * DELTA_THETA;
    let vertex: number[] = proj4(utm33).inverse([
      radius * Math.cos(t) + utm33coord[0],
      radius * Math.sin(t) + utm33coord[1],
    ]);
    coords.push(vertex);
  }
  return coords;
}

// pre:   route objects containtes the route
// post:  cen object contains the coords of the center
export function CenterExtents(cen: LngLat, route: RouteState): void {
  if (route.linePoints.length !== 0) {
    let minlat: number = route.linePoints[0].latitude;
    let maxlat: number = route.linePoints[0].latitude;
    let minlon: number = route.linePoints[0].longitude;
    let maxlon: number = route.linePoints[0].longitude;
    for (const point of route.linePoints) {
      if (point.latitude < minlat) minlat = point.latitude;
      if (point.latitude > maxlat) maxlat = point.latitude;
      if (point.longitude < minlat) minlon = point.longitude;
      if (point.longitude > maxlat) maxlon = point.longitude;
    }
    cen.lat = (minlat + maxlat) / 2.0;
    cen.lng = (minlon + maxlon) / 2.0;
  }
}
